/* eslint-disable func-names */
import React, { Component } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { Container, Row, Col, Card, Button, Form, Modal, Table, Badge } from "react-bootstrap";
import { FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaLayerGroup, FaBox, FaImage, FaChevronLeft, FaChevronRight, FaArrowLeft, FaInfoCircle, FaBolt, FaMinus, FaCog, FaEye, FaEyeSlash, FaCopy } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getTKT1FileUploadByID, getTKT1ONMDesignByID, createTKT1ONMDesign, updateTKT1ONMDesign } from '../actions';
import { MATERIAL_TYPES, MATERIAL_NAMES } from './TKT1ONMDesign';
import { test1AnalyzeSvg, calculateSvg2Transform, mergeSvgPaths } from '../utils/svgPathUtils';

class TKT1ONMDesignDetail extends Component {
  constructor(props) {
    super(props);
    this.state = {
      design: {
        id: null,
        name: '',
        description: '',
        layers: []
      },
      showLayerModal: false,
      showMaterialModal: false,
      showObjectModal: false,
      currentLayer: null,
      currentMaterial: null,
      currentObject: null,
      editingLayer: null,
      editingMaterial: null,
      editingObject: null,
      showRightPanel: true,
      previewZoom: 1,
      svgCache: {},
      loadingSvgs: {},
      loading: true,
      isNewDesign: false,
      showDesignInfoModal: false,
      objectEncodedPoints: [], // Encoded points for current object being edited
      quickEditObject: null, // Object being quick edited in Properties panel
      showControlPoints: true, // Show/hide control points in preview
      normalizeStrokeWidth: false, // Normalize stroke width to 1/scale
      showGrid: true, // Show/hide grid in preview
      showAxis: true, // Show/hide axis lines (center lines)
      showViewOptionsModal: false, // Show/hide settings modal
      sidebarLeftOffset: 20, // Left offset for preview controls (20px default, 270px when sidebar visible)
      isDragging: false, // Track if user is dragging the preview
      dragStartX: 0, // Mouse X position when drag starts
      dragStartY: 0, // Mouse Y position when drag starts
      dragStartScrollLeft: 0, // ScrollLeft when drag starts
      dragStartScrollTop: 0, // ScrollTop when drag starts
      designWidth: 3000, // Design canvas width in pixels
      designHeight: 3000, // Design canvas height in pixels
      visibleLayers: {}, // Track layer visibility { layerId: true/false }
      visibleMaterials: {}, // Track material visibility { layerId_materialType: true/false }
      visibleObjects: {}, // Track object visibility { objectId: true/false }
      showTest1Modal: false, // Show/hide Test 1 modal
      test1Results: null, // Results for Test 1
      test1TransformData: null, // Transform calculation data for SVG2
      test1MergedPath: null, // Merged path result
      test1Path1: null, // Path1 (from SVG1)
      test1Path2: null, // Path2 (from SVG2)
      test1P1: null, // p1 coordinates in SVG space {x, y}
      test1P2: null, // p2 coordinates in SVG space {x, y}
      test1P1Absolute: null, // P1 coordinates in absolute design space {x, y}
      test1P2Absolute: null // P2 coordinates in absolute design space {x, y}
    };
  }

  // Check sidebar visibility and update offset
  updateSidebarOffset = () => {
    const root = document.getElementById('root');
    const hasNoSidebarClass = root && root.classList.contains('no-sidebar');
    const sidebar = document.querySelector('.sidebar');
    const isSidebarVisible = sidebar && sidebar.offsetWidth > 0 && !hasNoSidebarClass;
    
    const newOffset = isSidebarVisible ? 270 : 20;
    if (this.state.sidebarLeftOffset !== newOffset) {
      this.setState({ sidebarLeftOffset: newOffset });
    }
  };

  componentDidMount() {
    // Load settings from localStorage
    this.loadSettingsFromStorage();
    
    // Add keyboard event listener for quick edit hotkeys
    document.addEventListener('keydown', this.handleKeyDown);
    
    // Check sidebar visibility
    this.updateSidebarOffset();
    
    // Listen for sidebar toggle (MutationObserver for class changes)
    this.sidebarObserver = new MutationObserver(() => {
      this.updateSidebarOffset();
    });
    
    const root = document.getElementById('root');
    if (root) {
      this.sidebarObserver.observe(root, {
        attributes: true,
        attributeFilter: ['class']
      });
    }
    
    // Also check on window resize
    window.addEventListener('resize', this.updateSidebarOffset);
    
    const { match } = this.props;
    const designId = match.params.designId;
    
    if (designId === 'new') {
      // New design
      this.setState({
        design: {
          id: null,
          name: '',
          description: '',
          layers: []
        },
        loading: false,
        isNewDesign: true
      });
    } else {
      // Load existing design
      this.loadDesign(designId);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    // When design changes, reload SVGs
    if (prevState.design !== this.state.design) {
      this.loadSvgsForPreview();
    }
    
    // Update SVG cache when currentFileUpload changes
    if (prevProps.currentFileUpload !== this.props.currentFileUpload && this.props.currentFileUpload) {
      const fileUpload = this.props.currentFileUpload;
      const svgId = fileUpload._id;
      
      if (svgId && !this.state.svgCache[svgId]) {
        let svgContent = null;
        let svgUrl = null;
        
        // Try normalized first, then original
        if (fileUpload.metadata?.normalizedSvgSource) {
          try {
            svgContent = atob(fileUpload.metadata.normalizedSvgSource);
          } catch (err) {
            console.error('Error decoding normalizedSvgSource:', err);
          }
        }
        
        if (!svgContent && fileUpload.metadata?.source_svg) {
          try {
            svgContent = atob(fileUpload.metadata.source_svg);
          } catch (err) {
            console.error('Error decoding source_svg:', err);
          }
        }
        
        // Get URL
        if (fileUpload.s3_key) {
          const bucketName = fileUpload.is_public ? 'tkt1-public-images' : 'tkt1-images';
          const baseUrl = `https://${bucketName}.s3.ap-southeast-2.amazonaws.com`;
          svgUrl = `${baseUrl}/${fileUpload.s3_key}`;
        }
        
        // Get SVG base size from viewBox (normalized SVG typically has viewBox="0 0 100 100")
        let svgBaseSize = 100; // Default for normalized SVG
        if (fileUpload.metadata?.originalViewBox?.w) {
          svgBaseSize = fileUpload.metadata.originalViewBox.w;
        } else if (svgContent) {
          // Try to parse viewBox from SVG content
          try {
            const parser = new DOMParser();
            const svgDoc = parser.parseFromString(svgContent, 'image/svg+xml');
            const svgElement = svgDoc.querySelector('svg');
            if (svgElement) {
              const viewBox = svgElement.getAttribute('viewBox');
              if (viewBox) {
                const parts = viewBox.split(' ');
                if (parts.length >= 4) {
                  svgBaseSize = Math.max(parseFloat(parts[2]) || 100, parseFloat(parts[3]) || 100);
                }
              } else {
                const width = svgElement.getAttribute('width');
                if (width) {
                  svgBaseSize = parseFloat(width) || 100;
                }
              }
            }
          } catch (err) {
            console.error('Error parsing SVG viewBox:', err);
          }
        }

        // Cache the SVG
        this.setState(prevState => ({
          svgCache: {
            ...prevState.svgCache,
            [svgId]: { 
              content: svgContent, 
              url: svgUrl,
              filename: fileUpload.filename || fileUpload.original_filename || null,
              baseSize: svgBaseSize
            }
          },
          loadingSvgs: {
            ...prevState.loadingSvgs,
            [svgId]: false
          }
        }));
      }
    }

    // Update design when currentDesign prop changes
    if (prevProps.currentDesign !== this.props.currentDesign && this.props.currentDesign) {
      this.setState({
        design: this.props.currentDesign,
        loading: false
      }, () => {
        this.loadSvgsForPreview();
      });
    }
  }

  componentWillUnmount() {
    // Remove keyboard event listener
    document.removeEventListener('keydown', this.handleKeyDown);
    
    // Remove sidebar observer
    if (this.sidebarObserver) {
      this.sidebarObserver.disconnect();
    }
    
    // Remove resize listener
    window.removeEventListener('resize', this.updateSidebarOffset);
    
    // Remove drag event listeners if still active
    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);
  }

  // Handle keyboard shortcuts for quick edit
  handleKeyDown = (e) => {
    // Only handle if quick edit is active
    if (!this.state.quickEditObject) return;
    
    // Check if focus is on an input element (to avoid conflicts)
    const activeElement = document.activeElement;
    const isInputFocused = activeElement && (
      activeElement.tagName === 'INPUT' || 
      activeElement.tagName === 'TEXTAREA' ||
      activeElement.isContentEditable
    );
    
    if (e.key === 'Escape') {
      // Escape: Close quick edit (only if not typing in an input outside quick edit)
      if (!isInputFocused || activeElement.closest('.quick-edit-row')) {
        e.preventDefault();
        this.handleCancelQuickEdit();
      }
    }
  };

  // Save design to localStorage
  saveDesignToStorage = () => {
    try {
      const { design } = this.state;
      if (design) {
        localStorage.setItem('onm_design_current', JSON.stringify(design));
      }
    } catch (error) {
      console.error('Error saving design to storage:', error);
    }
  };

  // Save settings to localStorage
  saveSettingsToStorage = () => {
    try {
      const settings = {
        designWidth: this.state.designWidth,
        designHeight: this.state.designHeight,
        showGrid: this.state.showGrid,
        showAxis: this.state.showAxis,
        showControlPoints: this.state.showControlPoints,
        normalizeStrokeWidth: this.state.normalizeStrokeWidth
      };
      localStorage.setItem('onm_design_settings', JSON.stringify(settings));
    } catch (error) {
      console.error('Error saving settings to storage:', error);
    }
  };

  // Load settings from localStorage
  loadSettingsFromStorage = () => {
    try {
      const stored = localStorage.getItem('onm_design_settings');
      if (stored) {
        const settings = JSON.parse(stored);
        this.setState({
          designWidth: settings.designWidth || 3000,
          designHeight: settings.designHeight || 3000,
          showGrid: settings.showGrid !== undefined ? settings.showGrid : true,
          showAxis: settings.showAxis !== undefined ? settings.showAxis : true,
          showControlPoints: settings.showControlPoints !== undefined ? settings.showControlPoints : true,
          normalizeStrokeWidth: settings.normalizeStrokeWidth !== undefined ? settings.normalizeStrokeWidth : false
        });
      }
    } catch (error) {
      console.error('Error loading settings from storage:', error);
    }
  };

  // Load design from localStorage
  loadDesignFromStorage = () => {
    try {
      const stored = localStorage.getItem('onm_design_current');
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('Error loading design from storage:', error);
    }
    return null;
  };

  loadDesign = async (designId) => {
    try {
      this.setState({ loading: true });
      await this.props.getTKT1ONMDesignByID(designId);
    } catch (error) {
      console.error('Error loading design:', error);
      toast.error('Lỗi khi tải design');
      this.setState({ loading: false });
    }
  };

  // Load SVG content for preview
  loadSvgsForPreview = async () => {
    const { design, svgCache } = this.state;
    const svgIds = new Set();
    
    // Collect all SVG IDs from all layers
    design.layers.forEach(layer => {
      layer.materials.forEach(material => {
        material.objects.forEach(obj => {
          if (obj.svg_id && !svgCache[obj.svg_id]) {
            svgIds.add(obj.svg_id);
          }
        });
      });
    });

    // Load each SVG
    for (const svgId of svgIds) {
      if (!this.state.loadingSvgs[svgId]) {
        this.setState(prevState => ({
          loadingSvgs: { ...prevState.loadingSvgs, [svgId]: true }
        }));
        
        try {
          await this.props.getTKT1FileUploadByID(svgId);
        } catch (error) {
          console.error(`Error loading SVG ${svgId}:`, error);
        }
      }
    }
  };

  // Get SVG URL from cache
  getSvgUrl = (svgId) => {
    return this.state.svgCache[svgId]?.url || null;
  };

  // Get SVG content from cache
  getSvgContent = (svgId) => {
    return this.state.svgCache[svgId]?.content || null;
  };

  // Get filename from cache, fallback to svgId
  getDisplayName = (svgId) => {
    const cached = this.state.svgCache[svgId];
    return cached?.filename || svgId;
  };

  // Get all objects from the same layer (excluding current object if editing)
  getReferenceObjectsInLayer = () => {
    const { currentLayer, currentObject, editingObject, design } = this.state;
    
    if (!currentLayer || !design || !design.layers) {
      return [];
    }

    // Find the layer in design
    const layer = design.layers.find(l => l.id === currentLayer.id);
    if (!layer || !layer.materials) {
      return [];
    }

    // Collect all objects from all materials in this layer
    const allObjects = [];
    layer.materials.forEach(material => {
      if (material.objects) {
        material.objects.forEach(obj => {
          // Exclude current object if editing
          if (!editingObject || obj.id !== editingObject.id) {
            allObjects.push({
              ...obj,
              materialType: material.type,
              materialName: material.name || material.type
            });
          }
        });
      }
    });

    return allObjects;
  };

  // Get SVG base size from cache
  getSvgBaseSize = (svgId) => {
    const cached = this.state.svgCache[svgId];
    return cached?.baseSize || 100; // Default to 100 for normalized SVG
  };

  // Parse encoded points from SVG (similar to TKT1SvgDetail.js)
  parseEncodedPointsForObject = (svgId) => {
    if (!svgId) return [];
    
    const svgContent = this.getSvgContent(svgId);
    if (!svgContent || (!svgContent.includes('EE') && !svgContent.includes('11AA'))) {
      return [];
    }

    const points = [];
    
    try {
      // Create a temporary DOM element to parse SVG
      const parser = new DOMParser();
      const svgDoc = parser.parseFromString(svgContent, 'image/svg+xml');
      
      // Extract style definitions
      const styles = {};
      const styleElements = svgDoc.querySelectorAll('style');
      styleElements.forEach(style => {
        const cssText = style.textContent || '';
        // Parse CSS rules like .fil0 {fill:#EE0101} or {fill:#11AA01}
        // Match both EE01XX and 11AAXX patterns
        const colorMatches = cssText.matchAll(/\.(\w+)\s*\{[^}]*fill:\s*#((?:EE\d{4})|(?:11AA\d{2}))/g);
        for (const match of colorMatches) {
          styles[match[1]] = match[2]; // class name -> color
        }
      });

      // Find all circles
      const circles = svgDoc.querySelectorAll('circle');
      circles.forEach(circle => {
        const cx = parseFloat(circle.getAttribute('cx'));
        const cy = parseFloat(circle.getAttribute('cy'));
        const classAttr = circle.getAttribute('class');
        
        if (isNaN(cx) || isNaN(cy)) return;
        
        let colorCode = null;
        
        // Try to get color from class
        if (classAttr && styles[classAttr.trim()]) {
          colorCode = styles[classAttr.trim()];
        } else {
          // Try to get color from style attribute
          const styleAttr = circle.getAttribute('style');
          if (styleAttr) {
            // Match both EE01XX and 11AAXX patterns
            const fillMatch = styleAttr.match(/fill:\s*#((?:EE\d{4})|(?:11AA\d{2}))/);
            if (fillMatch) {
              colorCode = fillMatch[1];
            }
          }
        }
        
        if (colorCode) {
          let typeName = 'Unknown';
          let typeCode = '';
          let idCode = '';
          
          if (colorCode.startsWith('EE01')) {
            // Snap Point: EE01XX (6 characters)
            typeName = 'Snap Point';
            typeCode = 'EE01';
            idCode = colorCode.substring(4, 6); // Last 2 digits
          } else if (colorCode.startsWith('11AA')) {
            // Center Point: 11AAXX (6 characters)
            typeName = 'Center Point';
            typeCode = '11AA';
            idCode = colorCode.substring(4, 6); // Last 2 digits
          } else if (colorCode.startsWith('EE')) {
            // Legacy format: EE[type:2digits][id:2digits]
            const legacyTypeCode = colorCode.substring(2, 4);
            idCode = colorCode.substring(4, 6);
            typeCode = `EE${legacyTypeCode}`;
            if (legacyTypeCode === '01') {
              typeName = 'Snap Point';
            } else if (legacyTypeCode === '02') {
              typeName = 'Text Center';
            }
          }
          
          if (typeName !== 'Unknown') {
            points.push({
              type: typeName,
              typeCode: typeCode,
              id: idCode,
              x: cx,
              y: cy,
              color: `#${colorCode}`
            });
          }
        }
      });
      
      // Sort by type, then by ID
      points.sort((a, b) => {
        if (a.typeCode !== b.typeCode) {
          return a.typeCode.localeCompare(b.typeCode);
        }
        return a.id.localeCompare(b.id);
      });
      
    } catch (error) {
      console.error('Error parsing encoded points:', error);
    }
    
    return points;
  };

  // Handle Test 1 button click
  handleTest1 = () => {
    const { design } = this.state;
    
    // Get first 2 SVG objects from the design
    const svgObjects = [];
    design.layers.forEach(layer => {
      layer.materials.forEach(material => {
        material.objects.forEach(obj => {
          if (obj.svg_id && svgObjects.length < 2) {
            svgObjects.push({
              svgId: obj.svg_id,
              objectId: obj.id,
              layerName: layer.name,
              materialName: material.name || material.type
            });
          }
        });
      });
    });
    
    if (svgObjects.length === 0) {
      toast.warning('Không tìm thấy SVG objects trong design');
      return;
    }
    
    const results = [];
    const objectData = {}; // Store object data for transform calculation
    
    svgObjects.forEach((svgObj) => {
      // Find the actual object in design to get its current scale and position
      let currentObject = null;
      design.layers.forEach(layer => {
        layer.materials.forEach(material => {
          material.objects.forEach(obj => {
            if (obj.id === svgObj.objectId) {
              currentObject = { ...obj, layer, material };
            }
          });
        });
      });

      const svgContent = this.getSvgContent(svgObj.svgId);
      
      if (!svgContent) {
        results.push({
          svgId: svgObj.svgId,
          objectId: svgObj.objectId,
          layerName: svgObj.layerName,
          materialName: svgObj.materialName,
          error: 'Không tìm thấy SVG content'
        });
        return;
      }
      
      // Get all encoded points (including snap points)
      const encodedPoints = this.parseEncodedPointsForObject(svgObj.svgId);
      
      // Use utility function to analyze SVG
      const analysis = test1AnalyzeSvg({
        svgContent,
        snapPoints: encodedPoints
      });
      
      const resultData = {
        svgId: svgObj.svgId,
        objectId: svgObj.objectId,
        layerName: svgObj.layerName,
        materialName: svgObj.materialName,
        scale: currentObject ? (parseFloat(currentObject.scale) || 1.0) : 1.0,
        center_x: currentObject ? (parseFloat(currentObject.center_x) || 0) : 0,
        center_y: currentObject ? (parseFloat(currentObject.center_y) || 0) : 0,
        currentObject: currentObject, // Store for later use
        ...analysis
      };
      
      results.push(resultData);
      objectData[svgObj.svgId] = resultData;
    });
    
    // Calculate transform for SVG2 if we have 2 SVGs
    let transformData = null;
    let mergedPathData = null;
    let path1Absolute = null;
    let path2Absolute = null;
    
    if (results.length === 2 && results[0].snapPoints && results[1].snapPoints) {
      transformData = calculateSvg2Transform(results[0], results[1]);
      
      // Calculate merged path if transform is successful
      if (transformData) {
        const svg1Content = this.getSvgContent(results[0].svgId);
        const svg2Content = this.getSvgContent(results[1].svgId);
        
        if (svg1Content && svg2Content) {
          // Get leftmost points (p1, p2) and second points (q1, q2)
          const svg1PathPoints = results[0].snapPoints
            .map(sp => sp.nearestPathPoint)
            .filter(pp => pp != null);
          const svg2PathPoints = results[1].snapPoints
            .map(sp => sp.nearestPathPoint)
            .filter(pp => pp != null);
          
          if (svg1PathPoints.length >= 2 && svg2PathPoints.length >= 2) {
            const p1 = svg1PathPoints.reduce((leftmost, point) => 
              point.x < leftmost.x ? point : leftmost
            );
            const q1 = svg1PathPoints
              .filter(p => p !== p1)
              .reduce((min, point) => 
                point.x < min.x ? point : (point.x === min.x && point.y < min.y ? point : min),
                svg1PathPoints.filter(p => p !== p1)[0]
              );
            
            const p2 = svg2PathPoints.reduce((leftmost, point) => 
              point.x < leftmost.x ? point : leftmost
            );
            const q2 = svg2PathPoints
              .filter(p => p !== p2)
              .reduce((min, point) => 
                point.x < min.x ? point : (point.x === min.x && point.y < min.y ? point : min),
                svg2PathPoints.filter(p => p !== p2)[0]
              );
            
            // Calculate merged path
            const mergedResult = mergeSvgPaths({
              svg1Content,
              svg2Content,
              p1,
              q1,
              p2,
              q2,
              svg1CenterX: results[0].center_x,
              svg1CenterY: results[0].center_y,
              svg1Scale: results[0].scale,
              svg2CenterX: transformData.cx2,
              svg2CenterY: transformData.cy2,
              svg2Scale: transformData.scale2
            });
            
            if (mergedResult && mergedResult.mergedPath) {
              mergedPathData = mergedResult.mergedPath;
              path1Absolute = mergedResult.path1;
              path2Absolute = mergedResult.path2;
              
              // Calculate absolute positions for P1 and P2
              // P1: p1 in SVG1 after transform
              const P1X = results[0].center_x + (p1.x - 50) * results[0].scale;
              const P1Y = results[0].center_y + (p1.y - 50) * results[0].scale;
              
              // P2: p2 in SVG2 after transform
              const P2X = transformData.cx2 + (p2.x - 50) * transformData.scale2;
              const P2Y = transformData.cy2 + (p2.y - 50) * transformData.scale2;
              
              // Store in state variables (including original paths)
              this.setState({
                test1P1: { x: p1.x, y: p1.y },
                test1P2: { x: p2.x, y: p2.y },
                test1P1Absolute: { x: P1X, y: P1Y },
                test1P2Absolute: { x: P2X, y: P2Y },
                test1Path1Original: mergedResult.path1Original,
                test1Path2Original: mergedResult.path2Original
              });
            }
          }
        }
      }
    }
    
    this.setState({
      showTest1Modal: true,
      test1Results: results,
      test1TransformData: transformData,
      test1MergedPath: mergedPathData,
      test1Path1: path1Absolute,
      test1Path2: path2Absolute
      // test1P1, test1P2, test1P1Absolute, test1P2Absolute will be set in the mergeSvgPaths block above
    });
  };

  // Handle Apply Transform button
  handleApplyTransform = () => {
    const { test1Results, test1TransformData, design } = this.state;
    
    if (!test1TransformData || !test1Results || test1Results.length < 2) {
      toast.error('Không có dữ liệu transform để apply');
      return;
    }

    // SVG2 is the second result (index 1)
    const svg2Result = test1Results[1];
    if (!svg2Result.currentObject) {
      toast.error('Không tìm thấy object SVG2 để update');
      return;
    }

    const { scale2, cx2, cy2 } = test1TransformData;
    
    // Find and update the object in design
    const layers = [...design.layers];
    let updated = false;

    layers.forEach((layer, layerIndex) => {
      layer.materials.forEach((material, materialIndex) => {
        material.objects.forEach((obj, objIndex) => {
          if (obj.id === svg2Result.objectId) {
            // Update object
            const updatedObjects = [...material.objects];
            updatedObjects[objIndex] = {
              ...obj,
              scale: scale2,
              center_x: cx2,
              center_y: cy2
            };
            
            const updatedMaterials = [...layer.materials];
            updatedMaterials[materialIndex] = {
              ...material,
              objects: updatedObjects
            };
            
            layers[layerIndex] = {
              ...layer,
              materials: updatedMaterials
            };
            
            updated = true;
          }
        });
      });
    });

    if (updated) {
      const updatedDesign = {
        ...design,
        layers
      };

      this.setState({
        design: updatedDesign
        // Don't close modal - let user close manually
      });

      toast.success('Đã cập nhật scale và vị trí cho SVG2!');
    } else {
      toast.error('Không tìm thấy object để update');
    }
  };

  // Design Management
  handleSaveDesign = async () => {
    const { design, isNewDesign } = this.state;
    
    if (!design.name) {
      toast.error('Vui lòng điền tên design');
      return;
    }

    try {
      // Save current scroll position before reloading
      let savedScrollPosition = null;
      if (!isNewDesign && this.previewContainerRef) {
        savedScrollPosition = {
          scrollLeft: this.previewContainerRef.scrollLeft,
          scrollTop: this.previewContainerRef.scrollTop
        };
      }

      this.setState({ loading: true });
      
      if (isNewDesign) {
        // Create new design
        const newDesign = {
          ...design,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        await this.props.createTKT1ONMDesign(newDesign);
        toast.success('Tạo design thành công!');
        // Redirect to list or reload with new ID
        setTimeout(() => {
          this.props.history.push('/admin/rnd/ornament/design');
        }, 1000);
      } else {
        // Update existing design
        const updatedDesign = {
          ...design,
          updated_at: new Date().toISOString()
        };
        await this.props.updateTKT1ONMDesign(design.id, updatedDesign);
        toast.success('Cập nhật design thành công!');
        
        // Reload design to get updated data
        await this.loadDesign(design.id);
        
        // Restore scroll position after a short delay to ensure DOM is updated
        if (savedScrollPosition && this.previewContainerRef) {
          setTimeout(() => {
            if (this.previewContainerRef) {
              this.previewContainerRef.scrollLeft = savedScrollPosition.scrollLeft;
              this.previewContainerRef.scrollTop = savedScrollPosition.scrollTop;
            }
          }, 100);
        }
      }
    } catch (error) {
      console.error('Error saving design:', error);
      toast.error('Lỗi khi lưu design');
    } finally {
      this.setState({ loading: false });
    }
  };

  // Layer Management
  handleAddLayer = () => {
    const newLayer = {
      id: `layer_${Date.now()}`,
      name: '',
      description: '',
      order: this.state.design.layers.length + 1,
      materials: []
    };

    this.setState({
      currentLayer: newLayer,
      editingLayer: null,
      showLayerModal: true
    });
  };

  handleEditLayer = (layer) => {
    this.setState({
      currentLayer: layer,
      editingLayer: layer,
      showLayerModal: true
    });
  };

  handleSaveLayer = () => {
    const { currentLayer, editingLayer, design } = this.state;

    if (!currentLayer.name) {
      toast.error('Vui lòng nhập tên layer');
      return;
    }

    const layers = [...design.layers];
    
    if (editingLayer) {
      // Update existing layer
      const index = layers.findIndex(l => l.id === editingLayer.id);
      if (index !== -1) {
        layers[index] = { ...currentLayer };
      }
    } else {
      // Add new layer
      layers.push(currentLayer);
    }

    const updatedDesign = {
      ...design,
      layers
    };

    this.setState({
      design: updatedDesign,
      showLayerModal: false,
      currentLayer: null,
      editingLayer: null
    });

    toast.success(`Đã ${editingLayer ? 'cập nhật' : 'thêm'} layer`);
  };

  handleDeleteLayer = (layerId) => {
    if (window.confirm('Bạn có chắc muốn xóa layer này?')) {
      const { design } = this.state;
      const layers = design.layers.filter(l => l.id !== layerId);

      const updatedDesign = {
        ...design,
        layers
      };

      this.setState({
        design: updatedDesign
      });

      toast.success('Đã xóa layer');
    }
  };

  // Material Management
  handleAddMaterial = (layer) => {
    const newMaterial = {
      type: '',
      name: '',
      objects: []
    };

    this.setState({
      currentLayer: layer,
      currentMaterial: newMaterial,
      editingMaterial: null,
      showMaterialModal: true
    });
  };

  handleEditMaterial = (layer, material) => {
    this.setState({
      currentLayer: layer,
      currentMaterial: material,
      editingMaterial: material,
      showMaterialModal: true
    });
  };

  handleSaveMaterial = () => {
    const { currentLayer, currentMaterial, editingMaterial, design } = this.state;

    if (!currentMaterial.type) {
      toast.error('Vui lòng chọn loại vật liệu');
      return;
    }

    const layers = [...design.layers];
    const layerIndex = layers.findIndex(l => l.id === currentLayer.id);
    
    if (layerIndex === -1) return;

    const materials = [...layers[layerIndex].materials];

    if (editingMaterial) {
      // Update existing material
      const materialIndex = materials.findIndex(m => m.type === editingMaterial.type);
      if (materialIndex !== -1) {
        materials[materialIndex] = {
          ...currentMaterial,
          name: MATERIAL_NAMES[currentMaterial.type] || currentMaterial.name
        };
      }
    } else {
      // Add new material
      materials.push({
        ...currentMaterial,
        name: MATERIAL_NAMES[currentMaterial.type] || currentMaterial.name
      });
    }

    layers[layerIndex].materials = materials;

    const updatedDesign = {
      ...design,
      layers
    };

    this.setState({
      design: updatedDesign,
      showMaterialModal: false,
      currentMaterial: null,
      editingMaterial: null
    });

    toast.success(`Đã ${editingMaterial ? 'cập nhật' : 'thêm'} vật liệu`);
  };

  handleDeleteMaterial = (layerId, materialType) => {
    if (window.confirm('Bạn có chắc muốn xóa vật liệu này?')) {
      const { design } = this.state;
      const layers = [...design.layers];
      const layerIndex = layers.findIndex(l => l.id === layerId);
      
      if (layerIndex !== -1) {
        layers[layerIndex].materials = layers[layerIndex].materials.filter(
          m => m.type !== materialType
        );

        const updatedDesign = {
          ...design,
          layers
        };

        this.setState({
          design: updatedDesign
        });

        toast.success('Đã xóa vật liệu');
      }
    }
  };

  // Object Management
  handleAddObject = (layer, material) => {
    const newObject = {
      id: `obj_${Date.now()}`,
      svg_id: '',
      center_x: '0',
      center_y: '0',
      scale: '1.0',
      rotation: '0',
      reference_object_id: null
      // boundingBoxSize không được lưu, chỉ tính để hiển thị: boundingBoxSize = scale * 100
    };

    this.setState({
      currentLayer: layer,
      currentMaterial: material,
      editingObject: null,
      showObjectModal: true,
      currentObject: newObject
    });
  };

  handleEditObject = (layer, material, object) => {
    let updatedObject = { ...object };
    
    // Remove boundingBoxSize if exists (chỉ lưu scale)
    delete updatedObject.boundingBoxSize;
    
    // Convert numeric values to strings for free editing
    updatedObject.center_x = updatedObject.center_x !== undefined ? String(updatedObject.center_x) : '0';
    updatedObject.center_y = updatedObject.center_y !== undefined ? String(updatedObject.center_y) : '0';
    updatedObject.scale = updatedObject.scale !== undefined ? String(updatedObject.scale) : '1.0';
    updatedObject.rotation = updatedObject.rotation !== undefined ? String(updatedObject.rotation) : '0';
    
    // Migrate from old width/height if exists
    if (updatedObject.width || updatedObject.height) {
      // Convert old width/height to scale (assuming base size 100)
      const oldSize = updatedObject.width || updatedObject.height || 100;
      updatedObject.scale = String(oldSize / 100);
      delete updatedObject.width;
      delete updatedObject.height;
    }
    
    // Parse encoded points from SVG
    const encodedPoints = this.parseEncodedPointsForObject(updatedObject.svg_id);
    
    this.setState({
      currentLayer: layer,
      currentMaterial: material,
      editingObject: object,
      showObjectModal: true,
      currentObject: updatedObject,
      objectEncodedPoints: encodedPoints
    });
  };

  handleSaveObject = () => {
    const { currentLayer, currentMaterial, currentObject, editingObject, design } = this.state;

    if (!currentObject.svg_id) {
      toast.error('Vui lòng nhập SVG ID');
      return;
    }

    const layers = [...design.layers];
    const layerIndex = layers.findIndex(l => l.id === currentLayer.id);
    
    if (layerIndex === -1) return;

    const materials = [...layers[layerIndex].materials];
    const materialIndex = materials.findIndex(m => m.type === currentMaterial.type);
    
    if (materialIndex === -1) return;

    const objects = [...materials[materialIndex].objects];

    // Prepare object to save - Parse string values to numbers when saving
    let scale = parseFloat(currentObject.scale) || 1.0;
    
    // If user edited bounding box, calculate scale from it
    if (currentObject._boundingBoxInput !== undefined) {
      const boundingBox = parseFloat(currentObject._boundingBoxInput);
      if (!isNaN(boundingBox)) {
        scale = boundingBox / 100;
      }
    }
    
    const objectToSave = { 
      ...currentObject,
      center_x: parseFloat(currentObject.center_x) || 0,
      center_y: parseFloat(currentObject.center_y) || 0,
      scale: scale,
      rotation: parseFloat(currentObject.rotation) || 0,
      reference_object_id: currentObject.reference_object_id || null
    };
    
    // Remove temporary fields and boundingBoxSize if exists
    delete objectToSave._boundingBoxInput;
    delete objectToSave.boundingBoxSize;
    
    if (editingObject) {
      // Update existing object
      const objectIndex = objects.findIndex(o => o.id === editingObject.id);
      if (objectIndex !== -1) {
        objects[objectIndex] = objectToSave;
      }
    } else {
      // Add new object
      objects.push(objectToSave);
    }

    materials[materialIndex].objects = objects;
    layers[layerIndex].materials = materials;

    const updatedDesign = {
      ...design,
      layers
    };

    this.setState({
      design: updatedDesign,
      showObjectModal: false,
      currentObject: null,
      editingObject: null
    });

    toast.success(`Đã ${editingObject ? 'cập nhật' : 'thêm'} object`);
  };

  handleDeleteObject = (layerId, materialType, objectId) => {
    if (window.confirm('Bạn có chắc muốn xóa object này?')) {
      const { design } = this.state;
      const layers = [...design.layers];
      const layerIndex = layers.findIndex(l => l.id === layerId);
      
      if (layerIndex !== -1) {
        const materials = [...layers[layerIndex].materials];
        const materialIndex = materials.findIndex(m => m.type === materialType);
        
        if (materialIndex !== -1) {
          materials[materialIndex].objects = materials[materialIndex].objects.filter(
            o => o.id !== objectId
          );
          layers[layerIndex].materials = materials;

          const updatedDesign = {
            ...design,
            layers
          };

          this.setState({
            design: updatedDesign
          });

          toast.success('Đã xóa object');
        }
      }
    }
  };

  // Quick Edit handlers
  handleQuickEdit = (layer, material, object) => {
    // Convert numeric values to strings for free editing
    const objectForEdit = {
      ...object,
      center_x: object.center_x !== undefined ? String(object.center_x) : '0',
      center_y: object.center_y !== undefined ? String(object.center_y) : '0',
      scale: object.scale !== undefined ? String(object.scale) : '1.0'
    };
    
    this.setState({
      quickEditObject: {
        layerId: layer.id,
        materialType: material.type,
        objectId: object.id,
        object: objectForEdit
      }
    });
  };

  handleSaveQuickEdit = () => {
    const { quickEditObject, design } = this.state;
    
    if (!quickEditObject) return;
    
    const { layerId, materialType, objectId, object } = quickEditObject;
    
    // Parse string values to numbers when saving
    const objectToSave = {
      ...object,
      center_x: parseFloat(object.center_x) || 0,
      center_y: parseFloat(object.center_y) || 0,
      scale: parseFloat(object.scale) || 1.0
    };
    
    const layers = [...design.layers];
    const layerIndex = layers.findIndex(l => l.id === layerId);
    
    if (layerIndex !== -1) {
      const materials = [...layers[layerIndex].materials];
      const materialIndex = materials.findIndex(m => m.type === materialType);
      
      if (materialIndex !== -1) {
        const objects = [...materials[materialIndex].objects];
        const objectIndex = objects.findIndex(o => o.id === objectId);
        
        if (objectIndex !== -1) {
          objects[objectIndex] = objectToSave;
          materials[materialIndex] = {
            ...materials[materialIndex],
            objects
          };
          layers[layerIndex] = {
            ...layers[layerIndex],
            materials
          };
          
          const updatedDesign = {
            ...design,
            layers
          };
          
          this.setState({ 
            design: updatedDesign,
            quickEditObject: null
          });
          this.saveDesignToStorage();
          toast.success('Cập nhật object thành công!');
        }
      }
    }
  };

  handleCancelQuickEdit = () => {
    this.setState({ quickEditObject: null });
  };

  // Preview functions
  handleZoomIn = () => {
    if (!this.previewContainerRef) {
      this.setState(prevState => ({
        previewZoom: Math.min(prevState.previewZoom + 0.25, 10)
      }));
      return;
    }
    
    // Save current scroll position relative to center (0,0)
    const container = this.previewContainerRef;
    const oldZoom = this.state.previewZoom;
    const currentScrollLeft = container.scrollLeft;
    const currentScrollTop = container.scrollTop;
    const centerX = container.scrollWidth / 2;
    const centerY = container.scrollHeight / 2;
    
    // Calculate offset from center
    const offsetX = currentScrollLeft - centerX;
    const offsetY = currentScrollTop - centerY;
    
    const newZoom = Math.min(oldZoom + 0.25, 10);
    
    this.setState({ previewZoom: newZoom }, () => {
      // After state update, adjust scroll to keep center (0,0) fixed
      setTimeout(() => {
        if (this.previewContainerRef) {
          const newCenterX = this.previewContainerRef.scrollWidth / 2;
          const newCenterY = this.previewContainerRef.scrollHeight / 2;
          const zoomRatio = newZoom / oldZoom;
          this.previewContainerRef.scrollLeft = newCenterX + offsetX * zoomRatio;
          this.previewContainerRef.scrollTop = newCenterY + offsetY * zoomRatio;
        }
      }, 0);
    });
  };

  handleZoomOut = () => {
    if (!this.previewContainerRef) {
      this.setState(prevState => ({
        previewZoom: Math.max(prevState.previewZoom - 0.25, 0.25)
      }));
      return;
    }
    
    // Save current scroll position relative to center (0,0)
    const container = this.previewContainerRef;
    const oldZoom = this.state.previewZoom;
    const currentScrollLeft = container.scrollLeft;
    const currentScrollTop = container.scrollTop;
    const centerX = container.scrollWidth / 2;
    const centerY = container.scrollHeight / 2;
    
    // Calculate offset from center
    const offsetX = currentScrollLeft - centerX;
    const offsetY = currentScrollTop - centerY;
    
    const newZoom = Math.max(oldZoom - 0.25, 0.25);
    
    this.setState({ previewZoom: newZoom }, () => {
      // After state update, adjust scroll to keep center (0,0) fixed
      setTimeout(() => {
        if (this.previewContainerRef) {
          const newCenterX = this.previewContainerRef.scrollWidth / 2;
          const newCenterY = this.previewContainerRef.scrollHeight / 2;
          const zoomRatio = newZoom / oldZoom;
          this.previewContainerRef.scrollLeft = newCenterX + offsetX * zoomRatio;
          this.previewContainerRef.scrollTop = newCenterY + offsetY * zoomRatio;
        }
      }, 0);
    });
  };

  handleResetZoom = () => {
    if (!this.previewContainerRef) {
      this.setState({ previewZoom: 1 });
      return;
    }
    
    // Save current scroll position relative to center (0,0)
    const container = this.previewContainerRef;
    const oldZoom = this.state.previewZoom;
    const currentScrollLeft = container.scrollLeft;
    const currentScrollTop = container.scrollTop;
    const centerX = container.scrollWidth / 2;
    const centerY = container.scrollHeight / 2;
    
    // Calculate offset from center
    const offsetX = currentScrollLeft - centerX;
    const offsetY = currentScrollTop - centerY;
    
    this.setState({ previewZoom: 1 }, () => {
      // After state update, adjust scroll to keep center (0,0) fixed
      setTimeout(() => {
        if (this.previewContainerRef) {
          const newCenterX = this.previewContainerRef.scrollWidth / 2;
          const newCenterY = this.previewContainerRef.scrollHeight / 2;
          const zoomRatio = 1 / oldZoom;
          this.previewContainerRef.scrollLeft = newCenterX + offsetX * zoomRatio;
          this.previewContainerRef.scrollTop = newCenterY + offsetY * zoomRatio;
        }
      }, 0);
    });
  };

  toggleRightPanel = () => {
    this.setState(prevState => ({
      showRightPanel: !prevState.showRightPanel
    }));
  };

  // Drag handlers for preview panning
  handleMouseDown = (e) => {
    // Only start dragging on left mouse button and if not clicking on buttons or interactive elements
    if (e.button !== 0 || e.target.tagName === 'BUTTON' || e.target.closest('button')) {
      return;
    }

    if (!this.previewContainerRef) return;

    e.preventDefault();
    this.setState({
      isDragging: true,
      dragStartX: e.clientX,
      dragStartY: e.clientY,
      dragStartScrollLeft: this.previewContainerRef.scrollLeft,
      dragStartScrollTop: this.previewContainerRef.scrollTop
    });

    // Change cursor to grab/grabbing
    if (this.previewContainerRef) {
      this.previewContainerRef.style.cursor = 'grabbing';
    }

    // Add event listeners for mouse move and up
    document.addEventListener('mousemove', this.handleMouseMove);
    document.addEventListener('mouseup', this.handleMouseUp);
  };

  handleMouseMove = (e) => {
    if (!this.state.isDragging || !this.previewContainerRef) return;

    e.preventDefault();

    // Calculate distance moved
    const deltaX = e.clientX - this.state.dragStartX;
    const deltaY = e.clientY - this.state.dragStartY;

    // Update scroll position
    this.previewContainerRef.scrollLeft = this.state.dragStartScrollLeft - deltaX;
    this.previewContainerRef.scrollTop = this.state.dragStartScrollTop - deltaY;
  };

  handleMouseUp = (e) => {
    if (!this.state.isDragging) return;

    // Remove event listeners
    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);

    // Reset cursor
    if (this.previewContainerRef) {
      this.previewContainerRef.style.cursor = 'grab';
    }

    this.setState({
      isDragging: false
    });
  };

  // Handle mouse wheel for zoom in/out at mouse position
  handleWheel = (e) => {
    if (!this.previewContainerRef) return;

    // Only zoom if holding Ctrl (or Cmd on Mac) - standard zoom behavior
    // If not holding Ctrl/Cmd, allow normal scrolling
    if (!e.ctrlKey && !e.metaKey) {
      return; // Allow normal scroll
    }

    // Prevent default scroll behavior when zooming
    e.preventDefault();

    const container = this.previewContainerRef;
    const oldZoom = this.state.previewZoom;

    // Calculate zoom delta (more sensitive zoom)
    const zoomDelta = e.deltaY > 0 ? -0.1 : 0.1;
    const newZoom = Math.max(0.25, Math.min(10, oldZoom + zoomDelta));

    if (newZoom === oldZoom) return;

    // Get mouse position relative to container viewport
    const rect = container.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    // Calculate the point on the canvas content that is under the mouse
    // Content coordinates = (scrollLeft + mouseX) / oldZoom
    const contentX = (container.scrollLeft + mouseX) / oldZoom;
    const contentY = (container.scrollTop + mouseY) / oldZoom;

    // Update zoom
    this.setState({ previewZoom: newZoom }, () => {
      // After zoom, adjust scroll to keep the same content point under the mouse
      setTimeout(() => {
        if (this.previewContainerRef) {
          // Calculate new scroll position to keep contentX, contentY under mouse
          const newScrollLeft = contentX * newZoom - mouseX;
          const newScrollTop = contentY * newZoom - mouseY;
          
          this.previewContainerRef.scrollLeft = newScrollLeft;
          this.previewContainerRef.scrollTop = newScrollTop;
        }
      }, 0);
    });
  };

  // Normalize stroke width in SVG content
  normalizeStrokeWidthInSvg = (svgContent, scale) => {
    if (!svgContent || !this.state.normalizeStrokeWidth || !scale || scale <= 0) {
      return svgContent;
    }

    try {
      const parser = new DOMParser();
      const svgDoc = parser.parseFromString(svgContent, 'image/svg+xml');
      const normalizedStrokeWidth = 1 / scale;

      // Update stroke-width in style definitions
      const styleElements = svgDoc.querySelectorAll('style');
      styleElements.forEach(style => {
        let cssText = style.textContent || '';
        // Replace stroke-width values in CSS rules
        // Match patterns like: stroke-width: 0.48; or stroke-width:2.61313
        cssText = cssText.replace(/stroke-width\s*:\s*([\d.]+)([^;}]*)/g, (match, value, rest) => {
          return `stroke-width: ${normalizedStrokeWidth}${rest}`;
        });
        style.textContent = cssText;
      });

      // Update stroke-width in inline style attributes of paths
      const paths = svgDoc.querySelectorAll('path');
      paths.forEach(path => {
        const styleAttr = path.getAttribute('style');
        if (styleAttr) {
          // Replace stroke-width in inline style
          const updatedStyle = styleAttr.replace(/stroke-width\s*:\s*([\d.]+)([^;]*)/g, (match, value, rest) => {
            return `stroke-width: ${normalizedStrokeWidth}${rest}`;
          });
          path.setAttribute('style', updatedStyle);
        } else {
          // Add stroke-width if no style attribute
          path.setAttribute('style', `stroke-width: ${normalizedStrokeWidth}`);
        }

        // Also check for stroke-width as a direct attribute
        if (path.hasAttribute('stroke-width')) {
          path.setAttribute('stroke-width', normalizedStrokeWidth);
        }
      });

      // Update stroke-width in other elements that might have stroke (circles, lines, etc.)
      const strokeElements = svgDoc.querySelectorAll('circle, line, rect, ellipse, polygon, polyline');
      strokeElements.forEach(el => {
        const styleAttr = el.getAttribute('style');
        if (styleAttr) {
          const updatedStyle = styleAttr.replace(/stroke-width\s*:\s*([\d.]+)([^;]*)/g, (match, value, rest) => {
            return `stroke-width: ${normalizedStrokeWidth}${rest}`;
          });
          el.setAttribute('style', updatedStyle);
        }

        if (el.hasAttribute('stroke-width')) {
          el.setAttribute('stroke-width', normalizedStrokeWidth);
        }
      });

      // Get the SVG element and return its HTML
      const svgElement = svgDoc.querySelector('svg');
      if (svgElement) {
        return new XMLSerializer().serializeToString(svgElement);
      }
    } catch (error) {
      console.error('Error normalizing stroke width:', error);
    }

    return svgContent;
  };

  // Remove control points from SVG content if needed
  filterControlPoints = (svgContent) => {
    if (!svgContent || this.state.showControlPoints) {
      return svgContent;
    }

    try {
      const parser = new DOMParser();
      const svgDoc = parser.parseFromString(svgContent, 'image/svg+xml');
      
      // Extract style definitions to identify control point colors
      const styles = {};
      const styleElements = svgDoc.querySelectorAll('style');
      styleElements.forEach(style => {
        const cssText = style.textContent || '';
        // Match both EE01XX and 11AAXX patterns
        const colorMatches = cssText.matchAll(/\.(\w+)\s*\{[^}]*fill:\s*#((?:EE\d{4})|(?:11AA\d{2}))/g);
        for (const match of colorMatches) {
          styles[match[1]] = match[2]; // class name -> color
        }
      });

      // Find and remove circles with control point colors
      const circles = svgDoc.querySelectorAll('circle');
      circles.forEach(circle => {
        const classAttr = circle.getAttribute('class');
        let shouldRemove = false;
        
        // Check if circle has control point color via class
        if (classAttr && styles[classAttr.trim()]) {
          const colorCode = styles[classAttr.trim()];
          if (colorCode.startsWith('EE') || colorCode.startsWith('11AA')) {
            shouldRemove = true;
          }
        } else {
          // Check if circle has control point color via style attribute
          const styleAttr = circle.getAttribute('style');
          if (styleAttr) {
            const fillMatch = styleAttr.match(/fill:\s*#((?:EE\d{4})|(?:11AA\d{2}))/);
            if (fillMatch) {
              shouldRemove = true;
            }
          }
        }
        
        if (shouldRemove) {
          circle.remove();
        }
      });

      // Get the SVG element and return its HTML
      const svgElement = svgDoc.querySelector('svg');
      if (svgElement) {
        return new XMLSerializer().serializeToString(svgElement);
      }
    } catch (error) {
      console.error('Error filtering control points:', error);
    }

    return svgContent;
  };

  renderPreview = () => {
    const { design, previewZoom } = this.state;

    if (!design.id || design.layers.length === 0) {
      return (
        <div className="d-flex align-items-center justify-content-center h-100" style={{ minHeight: '400px' }}>
          <div className="text-center text-muted">
            <FaImage size={48} className="mb-3" />
            <p>Chưa có layer để preview. Hãy thêm layer.</p>
          </div>
        </div>
      );
    }

    return (
      <div 
        ref={(el) => {
          // Save reference for scroll position restoration
          this.previewContainerRef = el;
          
          if (el && !this.scrollInitialized) {
            // Scroll to center when first rendered
            const scrollX = (el.scrollWidth - el.clientWidth) / 2;
            const scrollY = (el.scrollHeight - el.clientHeight) / 2;
            el.scrollLeft = scrollX;
            el.scrollTop = scrollY;
            this.scrollInitialized = true;
          }
        }}
        onMouseDown={this.handleMouseDown}
        onWheel={this.handleWheel}
        style={{ 
          width: '100%', 
          height: '100%', 
          overflow: 'auto',
          backgroundColor: '#f8f9fa',
          position: 'relative',
          cursor: this.state.isDragging ? 'grabbing' : 'grab',
          userSelect: 'none' // Prevent text selection while dragging
        }}
      >
        <div
          style={{
            width: `${this.state.designWidth * previewZoom}px`,
            height: `${this.state.designHeight * previewZoom}px`,
            position: 'relative',
            backgroundColor: '#ffffff',
            border: '1px solid #ddd',
            backgroundImage: this.state.showGrid ? `
              linear-gradient(to right, #e0e0e0 1px, transparent 1px),
              linear-gradient(to bottom, #e0e0e0 1px, transparent 1px)
            ` : 'none',
            backgroundSize: `${50 * previewZoom}px ${50 * previewZoom}px`,
            backgroundPosition: '0 0'
          }}
        >
          {/* Center lines (Axis) */}
          {this.state.showAxis && (
            <>
              <div
                style={{
                  position: 'absolute',
                  left: `${(this.state.designWidth / 2) * previewZoom}px`,
                  top: '0',
                  width: `${2 * previewZoom}px`,
                  height: '100%',
                  backgroundColor: '#999',
                  zIndex: 1
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  left: '0',
                  top: `${(this.state.designHeight / 2) * previewZoom}px`,
                  width: '100%',
                  height: `${2 * previewZoom}px`,
                  backgroundColor: '#999',
                  zIndex: 1
                }}
              />
            </>
          )}
          {/* Render layers in order */}
          {design.layers
            .sort((a, b) => a.order - b.order)
            .filter(layer => this.state.visibleLayers[layer.id] !== false)
            .map(layer => (
              <div key={layer.id} style={{ position: 'relative' }}>
                {layer.materials
                  .filter(material => this.state.visibleMaterials[`${layer.id}_${material.type}`] !== false)
                  .map(material => (
                  material.objects
                    .filter(obj => this.state.visibleObjects[obj.id] !== false)
                    .map(obj => {
                    const svgContent = this.getSvgContent(obj.svg_id);
                    const svgUrl = this.getSvgUrl(obj.svg_id);

                    if (svgContent) {
                      // Process SVG: filter control points and normalize stroke width
                      let processedSvgContent = this.filterControlPoints(svgContent);
                      const objScale = parseFloat(obj.scale) || 1;
                      processedSvgContent = this.normalizeStrokeWidthInSvg(processedSvgContent, objScale);
                      
                      return (
                        <div
                          key={obj.id}
                          style={{
                            position: 'absolute',
                            left: `${((this.state.designWidth / 2) + (obj.center_x || 0)) * previewZoom}px`,
                            top: `${((this.state.designHeight / 2) + (obj.center_y || 0)) * previewZoom}px`,
                            transform: `translate(-50%, -50%) scale(${(obj.scale || 1) * previewZoom}) rotate(${obj.rotation || 0}deg)`,
                            transformOrigin: 'center center',
                            zIndex: 2
                          }}
                          dangerouslySetInnerHTML={{ __html: processedSvgContent }}
                        />
                      );
                    } else if (svgUrl) {
                      return (
                        <img
                          key={obj.id}
                          src={svgUrl}
                          alt={`SVG ${obj.svg_id}`}
                          style={{
                            position: 'absolute',
                            left: `${((this.state.designWidth / 2) + (obj.center_x || 0)) * previewZoom}px`,
                            top: `${((this.state.designHeight / 2) + (obj.center_y || 0)) * previewZoom}px`,
                            transform: `translate(-50%, -50%) scale(${(obj.scale || 1) * previewZoom}) rotate(${obj.rotation || 0}deg)`,
                            transformOrigin: 'center center',
                            zIndex: 2
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      );
                    }
                    return null;
                  })
                ))}
              </div>
            ))}
        </div>
      </div>
    );
  };

  render() {
    const { 
      design, 
      showLayerModal, 
      showMaterialModal, 
      showObjectModal,
      currentLayer, 
      currentMaterial, 
      currentObject, 
      showRightPanel, 
      previewZoom,
      loading,
      showDesignInfoModal
    } = this.state;

    if (loading && !this.state.isNewDesign) {
      return (
        <Container fluid className="py-4">
          <Row>
            <Col className="text-center">
              <p>Đang tải design...</p>
            </Col>
          </Row>
        </Container>
      );
    }

    return (
      <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: '#f8f9fa', overflow: 'hidden' }}>
        {/* Full Screen Preview */}
        <div style={{ 
          width: '100%', 
          height: '100%', 
          position: 'relative',
          paddingRight: showRightPanel ? '400px' : '0'
        }}>
          {/* Preview Controls - Floating Top */}
          <div style={{
            position: 'absolute',
            top: '20px',
            left: `${this.state.sidebarLeftOffset}px`,
            zIndex: 1002,
            display: 'flex',
            gap: '10px',
            alignItems: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            padding: '10px 15px',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
          }}>
            <h5 className="mb-0 me-3">Design Preview</h5>
            <Button variant="outline-secondary" size="sm" onClick={this.handleZoomOut} disabled={previewZoom <= 0.25} title="Zoom Out">
              <FaMinus />
            </Button>
            <span className="text-muted small mx-2" style={{ minWidth: '50px', textAlign: 'center', display: 'inline-block' }}>
              {Math.round(previewZoom * 100)}%
            </span>
            <Button variant="outline-secondary" size="sm" onClick={this.handleResetZoom} title="Reset Zoom">
              Reset
            </Button>
            <Button variant="outline-secondary" size="sm" onClick={this.handleZoomIn} disabled={previewZoom >= 10} title="Zoom In">
              <FaPlus />
            </Button>
            <Button variant="outline-info" size="sm" onClick={() => this.setState({ showViewOptionsModal: true })} title="View Options" className="ms-2">
              <FaEdit />
            </Button>
          </div>

          {/* Preview Canvas - Full Screen */}
          <div style={{ width: '100%', height: '100%', overflow: 'hidden' }}>
            {this.renderPreview()}
          </div>
        </div>

        {/* Floating Action Buttons */}
        <Button
          variant="outline-secondary"
          onClick={() => this.props.history.push('/admin/rnd/ornament/design')}
          style={{
            position: 'fixed',
            top: '20px',
            right: showRightPanel ? '420px' : '20px',
            zIndex: 1002,
            boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
          }}
        >
          <FaArrowLeft className="me-2" />
          Quay lại
        </Button>

        <Button
          variant="primary"
          onClick={this.handleSaveDesign}
          style={{
            position: 'fixed',
            top: '70px',
            right: showRightPanel ? '420px' : '20px',
            zIndex: 1002,
            boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
          }}
        >
          <FaSave className="me-2" />
          Lưu Design
        </Button>

        {/* Info Icon Button */}
        <Button
          variant="outline-info"
          onClick={() => this.setState({ showDesignInfoModal: true })}
          style={{
            position: 'fixed',
            top: '120px',
            right: showRightPanel ? '420px' : '20px',
            zIndex: 1002,
            boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
          }}
          title="Thông tin Design"
        >
          <FaInfoCircle />
        </Button>

        {/* Toggle Button - Always visible when panel is collapsed */}
        {!showRightPanel && (
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={this.toggleRightPanel}
            style={{
              position: 'fixed',
              right: '0',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 1002,
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
              padding: '10px 5px',
              boxShadow: '-2px 0 10px rgba(0,0,0,0.1)'
            }}
            title="Mở Properties"
          >
            <FaChevronLeft />
          </Button>
        )}

        {/* Floating Right Panel */}
        <div
          style={{
            position: 'fixed',
            right: showRightPanel ? '0' : '-380px',
            top: '0',
            width: '400px',
            height: '100vh',
            backgroundColor: 'white',
            boxShadow: '-2px 0 10px rgba(0,0,0,0.1)',
            zIndex: 1001,
            transition: 'right 0.3s ease-in-out',
            overflowY: 'auto',
            padding: '20px'
          }}
        >
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Properties</h5>
            <div className="d-flex gap-2">
              <Button variant="outline-info" size="sm" onClick={this.handleTest1}>
                Test 1
              </Button>
              <Button variant="outline-secondary" size="sm" onClick={this.toggleRightPanel}>
                <FaChevronRight />
              </Button>
            </div>
          </div>


          {/* Design Info Card */}
          {design.id && (
            <Card className="mb-4">
              <Card.Header>
                <h6 className="mb-0">{design.name || 'Chưa có tên'}</h6>
              </Card.Header>
              <Card.Body>
                <p className="small mb-2"><strong>Mô tả:</strong> {design.description || '-'}</p>
                <p className="small mb-2"><strong>Ngày tạo:</strong> {design.created_at ? new Date(design.created_at).toLocaleString('vi-VN') : '-'}</p>
                <p className="small mb-0"><strong>Số layers:</strong> {design.layers.length}</p>
              </Card.Body>
            </Card>
          )}

          {/* Layers List */}
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Layers</h5>
              <FaPlus 
                className="text-primary" 
                style={{ cursor: 'pointer', fontSize: '16px' }}
                onClick={this.handleAddLayer}
                title="Thêm Layer"
              />
            </Card.Header>
            <Card.Body>
              {design.layers.length === 0 ? (
                <p className="text-muted text-center py-4">
                  Chưa có layer nào. Hãy thêm layer.
                </p>
              ) : (
                design.layers.map((layer, layerIdx) => (
                  <Card key={layer.id} className="mb-3">
                    <Card.Header className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="mb-0">
                          <FaLayerGroup className="me-2" />
                          {layer.name || `Layer ${layerIdx + 1}`}
                          <Badge bg="secondary" className="ms-2">Thứ tự: {layer.order}</Badge>
                        </h6>
                        {layer.description && <small className="text-muted">{layer.description}</small>}
                      </div>
                      <div className="d-flex gap-2 align-items-center">
                        {(this.state.visibleLayers[layer.id] !== false) ? (
                          <FaEye 
                            className="text-success" 
                            style={{ cursor: 'pointer', fontSize: '16px' }}
                            onClick={() => {
                              this.setState(prevState => ({
                                visibleLayers: {
                                  ...prevState.visibleLayers,
                                  [layer.id]: false
                                }
                              }));
                            }}
                            title="Ẩn Layer"
                          />
                        ) : (
                          <FaEyeSlash 
                            className="text-secondary" 
                            style={{ cursor: 'pointer', fontSize: '16px' }}
                            onClick={() => {
                              this.setState(prevState => ({
                                visibleLayers: {
                                  ...prevState.visibleLayers,
                                  [layer.id]: true
                                }
                              }));
                            }}
                            title="Hiện Layer"
                          />
                        )}
                        <FaEdit 
                          className="text-primary" 
                          style={{ cursor: 'pointer', fontSize: '16px' }}
                          onClick={() => this.handleEditLayer(layer)}
                          title="Edit Layer"
                        />
                        <FaTrash 
                          className="text-danger" 
                          style={{ cursor: 'pointer', fontSize: '16px' }}
                          onClick={() => this.handleDeleteLayer(layer.id)}
                          title="Delete Layer"
                        />
                      </div>
                    </Card.Header>
                    <Card.Body>
                      {/* Materials List */}
                      <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <strong>Vật liệu:</strong>
                          <FaPlus 
                            className="text-success" 
                            style={{ cursor: 'pointer', fontSize: '16px' }}
                            onClick={() => this.handleAddMaterial(layer)}
                            title="Thêm Vật liệu"
                          />
                        </div>
                        {layer.materials.length === 0 ? (
                          <p className="text-muted small">Chưa có vật liệu</p>
                        ) : (
                          layer.materials.map((material) => (
                            <Card key={material.type} className="mb-2">
                              <Card.Body className="py-2">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <div>
                                    <strong><FaBox className="me-1" />{material.name || material.type}</strong>
                                    <Badge bg="info" className="ms-2">{material.type}</Badge>
                                  </div>
                                  <div className="d-flex gap-2 align-items-center">
                                    {((this.state.visibleMaterials[`${layer.id}_${material.type}`] !== false)) ? (
                                      <FaEye 
                                        className="text-success" 
                                        style={{ cursor: 'pointer', fontSize: '16px' }}
                                        onClick={() => {
                                          this.setState(prevState => ({
                                            visibleMaterials: {
                                              ...prevState.visibleMaterials,
                                              [`${layer.id}_${material.type}`]: false
                                            }
                                          }));
                                        }}
                                        title="Ẩn Material"
                                      />
                                    ) : (
                                      <FaEyeSlash 
                                        className="text-secondary" 
                                        style={{ cursor: 'pointer', fontSize: '16px' }}
                                        onClick={() => {
                                          this.setState(prevState => ({
                                            visibleMaterials: {
                                              ...prevState.visibleMaterials,
                                              [`${layer.id}_${material.type}`]: true
                                            }
                                          }));
                                        }}
                                        title="Hiện Material"
                                      />
                                    )}
                                    <FaEdit 
                                      className="text-primary" 
                                      style={{ cursor: 'pointer', fontSize: '16px' }}
                                      onClick={() => this.handleEditMaterial(layer, material)}
                                      title="Edit Material"
                                    />
                                    <FaTrash 
                                      className="text-danger" 
                                      style={{ cursor: 'pointer', fontSize: '16px' }}
                                      onClick={() => this.handleDeleteMaterial(layer.id, material.type)}
                                      title="Delete Material"
                                    />
                                  </div>
                                </div>
                                {/* Objects List */}
                                <div>
                                  <div className="d-flex justify-content-between align-items-center mb-2">
                                    <small className="text-muted">Objects ({material.objects.length})</small>
                                    <FaPlus 
                                      className="text-success" 
                                      style={{ cursor: 'pointer', fontSize: '16px' }}
                                      onClick={() => this.handleAddObject(layer, material)}
                                      title="Thêm Object"
                                    />
                                  </div>
                                  {material.objects.length === 0 ? (
                                    <p className="text-muted small mb-0">Chưa có object</p>
                                  ) : (
                                    <Table striped bordered hover size="sm">
                                      <thead>
                                        <tr>
                                          <th>File Name</th>
                                          <th>Actions</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {material.objects.map((obj) => (
                                          <React.Fragment key={obj.id}>
                                            <tr>
                                              <td>
                                                {this.getDisplayName(obj.svg_id)}
                                              </td>
                                              <td>
                                                <div className="d-flex gap-2 align-items-center">
                                                  {(this.state.visibleObjects[obj.id] !== false) ? (
                                                    <FaEye 
                                                      className="text-success" 
                                                      style={{ cursor: 'pointer', fontSize: '16px' }}
                                                      onClick={() => {
                                                        this.setState(prevState => ({
                                                          visibleObjects: {
                                                            ...prevState.visibleObjects,
                                                            [obj.id]: false
                                                          }
                                                        }));
                                                      }}
                                                      title="Ẩn Object"
                                                    />
                                                  ) : (
                                                    <FaEyeSlash 
                                                      className="text-secondary" 
                                                      style={{ cursor: 'pointer', fontSize: '16px' }}
                                                      onClick={() => {
                                                        this.setState(prevState => ({
                                                          visibleObjects: {
                                                            ...prevState.visibleObjects,
                                                            [obj.id]: true
                                                          }
                                                        }));
                                                      }}
                                                      title="Hiện Object"
                                                    />
                                                  )}
                                                  <FaBolt 
                                                    className="text-warning" 
                                                    style={{ cursor: 'pointer', fontSize: '16px' }}
                                                    onClick={() => this.handleQuickEdit(layer, material, obj)}
                                                    title="Quick Edit"
                                                  />
                                                  <FaEdit 
                                                    className="text-primary" 
                                                    style={{ cursor: 'pointer', fontSize: '16px' }}
                                                    onClick={() => this.handleEditObject(layer, material, obj)}
                                                    title="Edit Full"
                                                  />
                                                  <FaTrash 
                                                    className="text-danger" 
                                                    style={{ cursor: 'pointer', fontSize: '16px' }}
                                                    onClick={() => this.handleDeleteObject(layer.id, material.type, obj.id)}
                                                    title="Delete"
                                                  />
                                                </div>
                                              </td>
                                            </tr>
                                            {/* Quick Edit Form Row */}
                                            {this.state.quickEditObject && 
                                             this.state.quickEditObject.layerId === layer.id && 
                                             this.state.quickEditObject.materialType === material.type && 
                                             this.state.quickEditObject.objectId === obj.id && (
                                              <tr>
                                                <td colSpan={2} className="p-2">
                                                  <div className="d-flex align-items-center gap-2">
                                                    <Form.Control
                                                      type="text"
                                                      size="sm"
                                                      placeholder="X"
                                                      style={{ width: '80px' }}
                                                      value={this.state.quickEditObject.object.center_x !== undefined ? String(this.state.quickEditObject.object.center_x) : ''}
                                                      onChange={(e) => {
                                                        const newObject = {
                                                          ...this.state.quickEditObject.object,
                                                          center_x: e.target.value
                                                        };
                                                        this.setState({
                                                          quickEditObject: {
                                                            ...this.state.quickEditObject,
                                                            object: newObject
                                                          }
                                                        });
                                                      }}
                                                      onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                          e.preventDefault();
                                                          this.handleSaveQuickEdit();
                                                        } else if (e.key === 'Escape') {
                                                          e.preventDefault();
                                                          this.handleCancelQuickEdit();
                                                        }
                                                      }}
                                                    />
                                                    <Form.Control
                                                      type="text"
                                                      size="sm"
                                                      placeholder="Y"
                                                      style={{ width: '80px' }}
                                                      value={this.state.quickEditObject.object.center_y !== undefined ? String(this.state.quickEditObject.object.center_y) : ''}
                                                      onChange={(e) => {
                                                        const newObject = {
                                                          ...this.state.quickEditObject.object,
                                                          center_y: e.target.value
                                                        };
                                                        this.setState({
                                                          quickEditObject: {
                                                            ...this.state.quickEditObject,
                                                            object: newObject
                                                          }
                                                        });
                                                      }}
                                                      onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                          e.preventDefault();
                                                          this.handleSaveQuickEdit();
                                                        } else if (e.key === 'Escape') {
                                                          e.preventDefault();
                                                          this.handleCancelQuickEdit();
                                                        }
                                                      }}
                                                    />
                                                    <Form.Control
                                                      type="text"
                                                      size="sm"
                                                      placeholder="Scale"
                                                      style={{ width: '80px' }}
                                                      value={this.state.quickEditObject.object.scale !== undefined ? String(this.state.quickEditObject.object.scale) : ''}
                                                      onChange={(e) => {
                                                        const newObject = {
                                                          ...this.state.quickEditObject.object,
                                                          scale: e.target.value
                                                        };
                                                        this.setState({
                                                          quickEditObject: {
                                                            ...this.state.quickEditObject,
                                                            object: newObject
                                                          }
                                                        });
                                                      }}
                                                      onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                          e.preventDefault();
                                                          this.handleSaveQuickEdit();
                                                        } else if (e.key === 'Escape') {
                                                          e.preventDefault();
                                                          this.handleCancelQuickEdit();
                                                        }
                                                      }}
                                                    />
                                                    <FaSave 
                                                      className="text-primary" 
                                                      style={{ cursor: 'pointer' }}
                                                      onClick={this.handleSaveQuickEdit}
                                                      title="Lưu"
                                                    />
                                                    <FaTimes 
                                                      className="text-secondary" 
                                                      style={{ cursor: 'pointer' }}
                                                      onClick={this.handleCancelQuickEdit}
                                                      title="Hủy"
                                                    />
                                                  </div>
                                                </td>
                                              </tr>
                                            )}
                                          </React.Fragment>
                                        ))}
                                      </tbody>
                                    </Table>
                                  )}
                                </div>
                              </Card.Body>
                            </Card>
                          ))
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                ))
              )}
            </Card.Body>
          </Card>
        </div>

        {/* Layer Modal */}
        <Modal show={showLayerModal} onHide={() => this.setState({ showLayerModal: false })}>
          <Modal.Header closeButton>
            <Modal.Title>{this.state.editingLayer ? 'Chỉnh sửa Layer' : 'Thêm Layer Mới'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Tên Layer *</Form.Label>
                <Form.Control
                  type="text"
                  value={currentLayer?.name || ''}
                  onChange={(e) => this.setState({ currentLayer: { ...currentLayer, name: e.target.value } })}
                  placeholder="Nhập tên layer"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Mô tả</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={currentLayer?.description || ''}
                  onChange={(e) => this.setState({ currentLayer: { ...currentLayer, description: e.target.value } })}
                  placeholder="Nhập mô tả"
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => this.setState({ showLayerModal: false })}>
              <FaTimes className="me-1" />
              Hủy
            </Button>
            <Button variant="primary" onClick={this.handleSaveLayer}>
              <FaSave className="me-1" />
              Lưu
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Material Modal */}
        <Modal show={showMaterialModal} onHide={() => this.setState({ showMaterialModal: false })}>
          <Modal.Header closeButton>
            <Modal.Title>{this.state.editingMaterial ? 'Chỉnh sửa Vật liệu' : 'Thêm Vật liệu Mới'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Loại Vật liệu *</Form.Label>
                <Form.Select
                  value={currentMaterial?.type || ''}
                  onChange={(e) => this.setState({ 
                    currentMaterial: { 
                      ...currentMaterial, 
                      type: e.target.value,
                      name: MATERIAL_NAMES[e.target.value] || ''
                    } 
                  })}
                >
                  <option value="">Chọn loại vật liệu</option>
                  {Object.values(MATERIAL_TYPES).map(type => (
                    <option key={type} value={type}>{MATERIAL_NAMES[type]}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => this.setState({ showMaterialModal: false })}>
              <FaTimes className="me-1" />
              Hủy
            </Button>
            <Button variant="primary" onClick={this.handleSaveMaterial}>
              <FaSave className="me-1" />
              Lưu
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Object Modal */}
        <Modal show={showObjectModal} onHide={() => this.setState({ showObjectModal: false, objectEncodedPoints: [] })} size="xl">
          <Modal.Header closeButton>
            <Modal.Title>{this.state.editingObject ? 'Chỉnh sửa Object' : 'Thêm Object Mới'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>SVG ID *</Form.Label>
                <Form.Control
                  type="text"
                  value={currentObject?.svg_id || ''}
                  onChange={(e) => {
                    const svgId = e.target.value;
                    // Parse encoded points when SVG ID changes
                    const encodedPoints = this.parseEncodedPointsForObject(svgId);
                    this.setState({ 
                      currentObject: { 
                        ...currentObject, 
                        svg_id: svgId
                      },
                      objectEncodedPoints: encodedPoints
                    });
                  }}
                  placeholder="Nhập ID của file SVG upload"
                />
              </Form.Group>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Center X</Form.Label>
                    <Form.Control
                      type="text"
                      value={currentObject?.center_x !== undefined ? String(currentObject.center_x) : ''}
                      onChange={(e) => {
                        this.setState({ 
                          currentObject: { 
                            ...currentObject, 
                            center_x: e.target.value
                          } 
                        });
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Center Y</Form.Label>
                    <Form.Control
                      type="text"
                      value={currentObject?.center_y !== undefined ? String(currentObject.center_y) : ''}
                      onChange={(e) => {
                        this.setState({ 
                          currentObject: { 
                            ...currentObject, 
                            center_y: e.target.value
                          } 
                        });
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Scale</Form.Label>
                    <Form.Control
                      type="text"
                      value={currentObject?.scale !== undefined ? String(currentObject.scale) : ''}
                      onChange={(e) => {
                        this.setState({ 
                          currentObject: { 
                            ...currentObject, 
                            scale: e.target.value
                          } 
                        });
                      }}
                    />
                    <Form.Text className="text-muted">
                      Bounding Box: {(currentObject?.scale || 1.0) * 100}
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Rotation (độ)</Form.Label>
                    <Form.Control
                      type="text"
                      value={currentObject?.rotation !== undefined ? String(currentObject.rotation) : ''}
                      onChange={(e) => {
                        this.setState({ 
                          currentObject: { 
                            ...currentObject, 
                            rotation: e.target.value
                          } 
                        });
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Kích thước Bounding Box</Form.Label>
                    <Form.Control
                      type="text"
                      value={currentObject?.scale ? String((parseFloat(currentObject.scale) || 1.0) * 100) : ''}
                      onChange={(e) => {
                        // Store bounding box input temporarily
                        this.setState({ 
                          currentObject: { 
                            ...currentObject, 
                            _boundingBoxInput: e.target.value
                          } 
                        });
                      }}
                    />
                    <Form.Text className="text-muted">
                      Scale: {currentObject?.scale || 1.0}
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              {/* Reference Object */}
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Object tham chiếu</Form.Label>
                    <Form.Select
                      value={currentObject?.reference_object_id || ''}
                      onChange={(e) => {
                        this.setState({ 
                          currentObject: { 
                            ...currentObject, 
                            reference_object_id: e.target.value || null
                          } 
                        });
                      }}
                    >
                      <option value="">-- Không có --</option>
                      {this.getReferenceObjectsInLayer().map(obj => (
                        <option key={obj.id} value={obj.id}>
                          {this.getDisplayName(obj.svg_id)} ({obj.materialName || obj.materialType})
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted">
                      Chọn object SVG khác trong cùng layer để tham chiếu
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              {/* Encoded Points Table */}
              {currentObject?.svg_id && this.state.objectEncodedPoints && this.state.objectEncodedPoints.length > 0 && (
                <div className="mt-4">
                  <h6>Control Points</h6>
                  <div className="table-responsive">
                    <Table striped bordered hover size="sm">
                      <thead>
                        <tr>
                          <th>Type</th>
                          <th>ID</th>
                          <th>X (cx)</th>
                          <th>Y (cy)</th>
                          <th>Color</th>
                        </tr>
                      </thead>
                      <tbody>
                        {this.state.objectEncodedPoints.map((point, index) => {
                          // Get badge color for each type
                          const getTypeBadgeColor = (typeCode) => {
                            if (typeCode === 'EE01') {
                              return 'primary'; // Blue for Snap Point
                            } else if (typeCode === '11AA') {
                              return 'success'; // Green for Center Point
                            } else if (typeCode === 'EE02') {
                              return 'warning'; // Orange for Text Center
                            } else {
                              return 'info'; // Cyan for other types
                            }
                          };

                          return (
                            <tr key={index}>
                              <td>
                                <Badge bg={getTypeBadgeColor(point.typeCode)}>
                                  {point.type}
                                </Badge>
                              </td>
                              <td>{point.id}</td>
                              <td>{point.x}</td>
                              <td>{point.y}</td>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  <div
                                    style={{
                                      width: '20px',
                                      height: '20px',
                                      backgroundColor: point.color,
                                      border: '1px solid #dee2e6',
                                      borderRadius: '3px'
                                    }}
                                  />
                                  <span>{point.color}</span>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                  <div className="mt-2 small text-muted">
                    <strong>Quy ước mã hóa:</strong>
                    <br />
                    <strong>Snap Point:</strong> <code>#EE01XX</code> (XX là ID, 2 chữ số)
                    <br />
                    <strong>Center Point:</strong> <code>#11AAXX</code> (XX là ID, 2 chữ số)
                  </div>
                </div>
              )}
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => this.setState({ showObjectModal: false, objectEncodedPoints: [] })}>
              <FaTimes className="me-1" />
              Hủy
            </Button>
            <Button variant="primary" onClick={this.handleSaveObject}>
              <FaSave className="me-1" />
              Lưu
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Design Info Modal */}
        <Modal show={showDesignInfoModal} onHide={() => this.setState({ showDesignInfoModal: false })}>
          <Modal.Header closeButton>
            <Modal.Title>Thông tin Design</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Tên Design *</Form.Label>
                <Form.Control
                  type="text"
                  value={design.name}
                  onChange={(e) => this.setState({ design: { ...design, name: e.target.value } })}
                  placeholder="Nhập tên design"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Mô tả</Form.Label>
                <Form.Control
                  type="text"
                  value={design.description}
                  onChange={(e) => this.setState({ design: { ...design, description: e.target.value } })}
                  placeholder="Nhập mô tả"
                />
              </Form.Group>
              {design.id && (
                <div className="mt-3">
                  <p className="small mb-2"><strong>Ngày tạo:</strong> {design.created_at ? new Date(design.created_at).toLocaleString('vi-VN') : '-'}</p>
                  <p className="small mb-0"><strong>Số layers:</strong> {design.layers.length}</p>
                </div>
              )}
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => this.setState({ showDesignInfoModal: false })}>
              Đóng
            </Button>
            <Button variant="primary" onClick={() => {
              this.setState({ showDesignInfoModal: false });
              this.handleSaveDesign();
            }}>
              <FaSave className="me-1" />
              Lưu
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Settings Modal */}
        <Modal show={this.state.showViewOptionsModal} onHide={() => this.setState({ showViewOptionsModal: false })} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Settings</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              {/* Design Size Settings */}
              <Card className="mb-4">
                <Card.Header>
                  <h6 className="mb-0">Design Size</h6>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Width (px)</Form.Label>
                        <Form.Control
                          type="number"
                          min="100"
                          max="10000"
                          step="100"
                          value={this.state.designWidth}
                          onChange={(e) => {
                            const value = parseInt(e.target.value, 10);
                            if (!isNaN(value) && value >= 100) {
                              this.setState({ designWidth: value }, () => {
                                this.saveSettingsToStorage();
                              });
                            }
                          }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Height (px)</Form.Label>
                        <Form.Control
                          type="number"
                          min="100"
                          max="10000"
                          step="100"
                          value={this.state.designHeight}
                          onChange={(e) => {
                            const value = parseInt(e.target.value, 10);
                            if (!isNaN(value) && value >= 100) {
                              this.setState({ designHeight: value }, () => {
                                this.saveSettingsToStorage();
                              });
                            }
                          }}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* View Options */}
              <Card>
                <Card.Header>
                  <h6 className="mb-0">View Options</h6>
                </Card.Header>
                <Card.Body>
                  <Form.Check
                    type="checkbox"
                    id="viewOptionShowGrid"
                    label="Show Grid"
                    checked={this.state.showGrid}
                    onChange={(e) => {
                      this.setState({ showGrid: e.target.checked }, () => {
                        this.saveSettingsToStorage();
                      });
                    }}
                    className="mb-3"
                  />
                  <Form.Check
                    type="checkbox"
                    id="viewOptionShowAxis"
                    label="Show Axis"
                    checked={this.state.showAxis}
                    onChange={(e) => {
                      this.setState({ showAxis: e.target.checked }, () => {
                        this.saveSettingsToStorage();
                      });
                    }}
                    className="mb-3"
                  />
                  <Form.Check
                    type="checkbox"
                    id="viewOptionShowControlPoints"
                    label="Show Control Points"
                    checked={this.state.showControlPoints}
                    onChange={(e) => {
                      this.setState({ showControlPoints: e.target.checked }, () => {
                        this.saveSettingsToStorage();
                      });
                    }}
                    className="mb-3"
                  />
                  <Form.Check
                    type="checkbox"
                    id="viewOptionNormalizeStrokeWidth"
                    label="Normalized Stroke Width"
                    checked={this.state.normalizeStrokeWidth}
                    onChange={(e) => {
                      this.setState({ normalizeStrokeWidth: e.target.checked }, () => {
                        this.saveSettingsToStorage();
                      });
                    }}
                  />
                </Card.Body>
              </Card>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => this.setState({ showViewOptionsModal: false })}>
              Đóng
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Test 1 Modal */}
        <Modal show={this.state.showTest1Modal} onHide={() => this.setState({ showTest1Modal: false, test1Results: null, test1TransformData: null, test1MergedPath: null, test1Path1: null, test1Path2: null, test1P1: null, test1P2: null, test1P1Absolute: null, test1P2Absolute: null, test1Path1Original: null, test1Path2Original: null })} size="xl">
          <Modal.Header closeButton>
            <Modal.Title>Test 1: Snap Points và Path Points</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            {/* Transform Calculation Result */}
            {this.state.test1TransformData && (
              <Card className="mb-4 border-primary">
                <Card.Header className="bg-primary text-white">
                  <h6 className="mb-0">Transform Calculation (SVG2 theo SVG1)</h6>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <p className="mb-2">
                        <strong>Length1 (SVG1):</strong> {this.state.test1TransformData.length1?.toFixed(2) || 'N/A'}
                      </p>
                      <p className="mb-2">
                        <strong>Length2 (SVG2):</strong> {this.state.test1TransformData.length2?.toFixed(2) || 'N/A'}
                      </p>
                      <p className="mb-2">
                        <strong>Ratio:</strong> {this.state.test1TransformData.ratio?.toFixed(4) || 'N/A'}
                      </p>
                    </Col>
                    <Col md={6}>
                      <p className="mb-2">
                        <strong>New Scale (SVG2):</strong> <Badge bg="info">{this.state.test1TransformData.scale2?.toFixed(4) || 'N/A'}</Badge>
                      </p>
                      <p className="mb-2">
                        <strong>New Center X (SVG2):</strong> <Badge bg="info">{this.state.test1TransformData.cx2?.toFixed(2) || 'N/A'}</Badge>
                      </p>
                      <p className="mb-2">
                        <strong>New Center Y (SVG2):</strong> <Badge bg="info">{this.state.test1TransformData.cy2?.toFixed(2) || 'N/A'}</Badge>
                      </p>
                    </Col>
                  </Row>
                  <div className="mt-3">
                    <Button variant="primary" onClick={this.handleApplyTransform}>
                      <FaSave className="me-2" />
                      Apply Transform
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            )}

            {/* Merged Path Display */}
            {this.state.test1MergedPath && (
              <Card className="mb-4 border-success">
                <Card.Header className="bg-success text-white">
                  <h6 className="mb-0">Merged Path</h6>
                </Card.Header>
                <Card.Body>
                  {/* Point Coordinates */}
                  <Row className="mb-3">
                    <Col md={6}>
                      <h6>Point Coordinates (SVG Space):</h6>
                      <p className="mb-1">
                        <strong>p1:</strong> ({this.state.test1P1?.x?.toFixed(4) || 'N/A'}, {this.state.test1P1?.y?.toFixed(4) || 'N/A'})
                      </p>
                      <p className="mb-0">
                        <strong>p2:</strong> ({this.state.test1P2?.x?.toFixed(4) || 'N/A'}, {this.state.test1P2?.y?.toFixed(4) || 'N/A'})
                      </p>
                    </Col>
                    <Col md={6}>
                      <h6>Point Coordinates (Absolute Design Space):</h6>
                      <p className="mb-1">
                        <strong>P1:</strong> ({this.state.test1P1Absolute?.x?.toFixed(2) || 'N/A'}, {this.state.test1P1Absolute?.y?.toFixed(2) || 'N/A'})
                      </p>
                      <p className="mb-0">
                        <strong>P2:</strong> ({this.state.test1P2Absolute?.x?.toFixed(2) || 'N/A'}, {this.state.test1P2Absolute?.y?.toFixed(2) || 'N/A'})
                      </p>
                    </Col>
                  </Row>
                  <hr />
                  <Row>
                    <Col md={12} className="mb-3">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <h6 className="mb-0">Path 1 Original (SVG1 - SVG Space):</h6>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => {
                            navigator.clipboard.writeText(this.state.test1Path1Original || '');
                            toast.success('Đã copy Path 1 Original!');
                          }}
                        >
                          <FaCopy className="me-1" />
                          Copy
                        </Button>
                      </div>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={this.state.test1Path1Original || 'N/A'}
                        readOnly
                        style={{ fontFamily: 'monospace', fontSize: '12px' }}
                      />
                    </Col>
                    <Col md={12} className="mb-3">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <h6 className="mb-0">Path 2 Original (SVG2 - SVG Space):</h6>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => {
                            navigator.clipboard.writeText(this.state.test1Path2Original || '');
                            toast.success('Đã copy Path 2 Original!');
                          }}
                        >
                          <FaCopy className="me-1" />
                          Copy
                        </Button>
                      </div>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={this.state.test1Path2Original || 'N/A'}
                        readOnly
                        style={{ fontFamily: 'monospace', fontSize: '12px' }}
                      />
                    </Col>
                    <Col md={12} className="mb-3">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <h6 className="mb-0">Path 1 (SVG1 - Absolute):</h6>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => {
                            navigator.clipboard.writeText(this.state.test1Path1 || '');
                            toast.success('Đã copy Path 1!');
                          }}
                        >
                          <FaCopy className="me-1" />
                          Copy
                        </Button>
                      </div>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={this.state.test1Path1 || 'N/A'}
                        readOnly
                        style={{ fontFamily: 'monospace', fontSize: '12px' }}
                      />
                    </Col>
                    <Col md={12} className="mb-3">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <h6 className="mb-0">Path 2 (SVG2 - Absolute):</h6>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => {
                            navigator.clipboard.writeText(this.state.test1Path2 || '');
                            toast.success('Đã copy Path 2!');
                          }}
                        >
                          <FaCopy className="me-1" />
                          Copy
                        </Button>
                      </div>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={this.state.test1Path2 || 'N/A'}
                        readOnly
                        style={{ fontFamily: 'monospace', fontSize: '12px' }}
                      />
                    </Col>
                    <Col md={12}>
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <h6 className="mb-0">Merged Path (Absolute):</h6>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => {
                            navigator.clipboard.writeText(this.state.test1MergedPath || '');
                            toast.success('Đã copy Merged Path!');
                          }}
                        >
                          <FaCopy className="me-1" />
                          Copy
                        </Button>
                      </div>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        value={this.state.test1MergedPath}
                        readOnly
                        style={{ fontFamily: 'monospace', fontSize: '12px' }}
                      />
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            )}

            {this.state.test1Results && this.state.test1Results.length > 0 ? (
              this.state.test1Results.map((result, svgIndex) => (
                <Card key={svgIndex} className="mb-4">
                  <Card.Header>
                    <h6 className="mb-0">
                      SVG {svgIndex + 1}: {this.getDisplayName(result.svgId)}
                    </h6>
                    <small className="text-muted">
                      Layer: {result.layerName || '-'} | Material: {result.materialName || '-'}
                      {(result.scale !== undefined || result.center_x !== undefined) && (
                        <span className="ms-3">
                          | Scale: {result.scale?.toFixed(2) || '1.00'} | 
                          Center: ({result.center_x?.toFixed(2) || '0'}, {result.center_y?.toFixed(2) || '0'})
                        </span>
                      )}
                    </small>
                  </Card.Header>
                  <Card.Body>
                    {result.error ? (
                      <div className="text-danger">{result.error}</div>
                    ) : (
                      <>
                        <div className="mb-2">
                          <strong>Snap Points:</strong> {result.snapPoints?.length || 0}
                          {result.totalPathPoints !== undefined && (
                            <span className="ms-3">
                              <strong>Total Path Points:</strong> {result.totalPathPoints}
                            </span>
                          )}
                        </div>
                        {result.snapPoints && result.snapPoints.length > 0 ? (
                          <div className="table-responsive">
                            <Table striped bordered hover size="sm">
                              <thead>
                                <tr>
                                  <th>Snap Point ID</th>
                                  <th>Snap Point (x, y)</th>
                                  <th>Nearest Path Point (x, y)</th>
                                  <th>Distance</th>
                                  <th>Path Point Type</th>
                                </tr>
                              </thead>
                              <tbody>
                                {result.snapPoints.map((item, index) => (
                                  <tr key={index}>
                                    <td>{item.snapPoint.id}</td>
                                    <td>
                                      ({item.snapPoint.x.toFixed(2)}, {item.snapPoint.y.toFixed(2)})
                                    </td>
                                    <td>
                                      {item.nearestPathPoint ? (
                                        <>
                                          ({item.nearestPathPoint.x.toFixed(2)}, {item.nearestPathPoint.y.toFixed(2)})
                                        </>
                                      ) : (
                                        <span className="text-muted">N/A</span>
                                      )}
                                    </td>
                                    <td>
                                      {item.nearestPathPoint ? (
                                        <Badge bg={item.nearestPathPoint.distance < 1 ? 'success' : item.nearestPathPoint.distance < 5 ? 'warning' : 'danger'}>
                                          {item.nearestPathPoint.distance.toFixed(2)}
                                        </Badge>
                                      ) : (
                                        <span className="text-muted">N/A</span>
                                      )}
                                    </td>
                                    <td>
                                      {item.nearestPathPoint ? (
                                        <Badge bg="info">{item.nearestPathPoint.type}</Badge>
                                      ) : (
                                        <span className="text-muted">N/A</span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                        ) : (
                          <p className="text-muted">Không có snap points</p>
                        )}
                      </>
                    )}
                  </Card.Body>
                </Card>
              ))
            ) : (
              <p className="text-muted">Không có dữ liệu</p>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => this.setState({ showTest1Modal: false, test1Results: null, test1TransformData: null, test1MergedPath: null, test1Path1: null, test1Path2: null, test1P1: null, test1P2: null, test1P1Absolute: null, test1P2Absolute: null, test1Path1Original: null, test1Path2Original: null })}>
              Đóng
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    currentDesign: state.tkt1OnmDesignReducer.currentDesign,
    currentFileUpload: state.tkt1FileUploadManagementReducer.currentFileUpload
  };
};

export default withRouter(connect(mapStateToProps, { 
  getTKT1FileUploadByID, 
  getTKT1ONMDesignByID, 
  createTKT1ONMDesign, 
  updateTKT1ONMDesign 
})(TKT1ONMDesignDetail));

